import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import AccountConfirmed from "../../components/icons/AccountConfirmed";
import ConfirmationPending from "../../components/icons/ConfirmationPending";
import IOSSwitch from "../../components/SwitchButton";
import moment from "moment";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  requestUpdateAccountCredit,
  requestUpdateUserStatus,
} from "../../redux/actions/tabManagementActionCreators";

const UserDetails = ({
  open,
  handleClose,
  userDetails,
  page,
  searchText,
  enterprise,
  status,
  type,
  handeleUserDetails,
}) => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const canEditCredits = currentUser?.role === "cytailor_admin";
  const isEnterpriseAccount = userDetails?.type === "enterprise";

  const [isSavingAccountCredit, setIsSavingAccountCredit] = useState(false);
  const [creditInputError, setCreditInputError] = useState("");
  const [creditForm, setCreditForm] = useState({
    tier: "tier_1",
    remaining_credits: "",
  });
  const hasEditableCreditValue =
    creditForm.remaining_credits !== "" &&
    creditForm.remaining_credits !== null &&
    creditForm.remaining_credits !== undefined;
  const hasPersistedCreditValues =
    userDetails?.credit_remaining !== null &&
    userDetails?.credit_remaining !== undefined;
  const shouldShowCreditControls =
    hasEditableCreditValue || hasPersistedCreditValues || Boolean(userDetails?.status);
  const reservedCredits = useMemo(() => {
    const parsed = Number(userDetails?.credit_reserved);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [userDetails?.credit_reserved]);
  const nextRemainingCredits = useMemo(() => {
    const parsed = Number(creditForm.remaining_credits);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [creditForm.remaining_credits]);
  const persistedInitialCredits = useMemo(() => {
    const parsed = Number(userDetails?.credit_remaining);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [userDetails?.credit_remaining]);
  const initialCreditsForDisplay =
    nextRemainingCredits !== null ? nextRemainingCredits : persistedInitialCredits;
  const remainingAfterReportsGeneration = Math.max(
    initialCreditsForDisplay - reservedCredits,
    0
  );
  const hasZeroCurrentCredits = initialCreditsForDisplay === 0;
  const showReservedCreditField = initialCreditsForDisplay > 0;
  const showRemainingCreditField =
    initialCreditsForDisplay > 0 && reservedCredits > 0;

  useEffect(() => {
    if (!open || !isEnterpriseAccount) {
      return;
    }
    setCreditForm({
      tier: userDetails?.credit_tier || "tier_1",
      remaining_credits:
        userDetails?.credit_remaining !== null &&
        userDetails?.credit_remaining !== undefined
          ? Number(userDetails?.credit_remaining)
          : "",
    });
    const persistedRemaining = Number(userDetails?.credit_remaining);
    const persistedReserved = Number(userDetails?.credit_reserved);
    if (
      Number.isFinite(persistedRemaining) &&
      Number.isFinite(persistedReserved) &&
      persistedRemaining < persistedReserved
    ) {
      setCreditInputError(
        `Current credits cannot be lower than reserved credits (${persistedReserved}).`
      );
    } else {
      setCreditInputError("");
    }
  }, [
    isEnterpriseAccount,
    open,
    userDetails?.credit_remaining,
    userDetails?.credit_reserved,
    userDetails?.credit_tier,
  ]);

  const handleSaveAccountCredit = async () => {
    if (!userDetails?.status || !isEnterpriseAccount) {
      return;
    }
    const parsedRemaining = Number(creditForm.remaining_credits);
    if (
      !creditForm.tier ||
      Boolean(creditInputError) ||
      !Number.isFinite(parsedRemaining) ||
      parsedRemaining < 0
    ) {
      return;
    }
    if (parsedRemaining < reservedCredits) {
      setCreditInputError(
        `Current credits cannot be lower than reserved credits (${reservedCredits}).`
      );
      return;
    }
    setIsSavingAccountCredit(true);
    try {
      const response = await dispatch(
        requestUpdateAccountCredit(
          userDetails?.public_id,
          {
            tier: creditForm.tier,
            remaining_credits: parsedRemaining,
          },
          page,
          searchText,
          enterprise,
          status,
          type
        )
      );
      if (response?.credit) {
        const updatedCreditForm = {
          tier: response?.credit?.tier || "tier_1",
          remaining_credits:
            response?.credit?.remaining_credits !== null &&
            response?.credit?.remaining_credits !== undefined
              ? Number(response?.credit?.remaining_credits)
              : "",
        };
        setCreditForm(updatedCreditForm);
        const responseReservedCredits =
          response?.credit?.reserved_credits !== null &&
          response?.credit?.reserved_credits !== undefined
            ? Number(response?.credit?.reserved_credits)
            : 0;
        const responseAvailableCredits =
          response?.credit?.available_credits !== null &&
          response?.credit?.available_credits !== undefined
            ? Number(response?.credit?.available_credits)
            : Math.max(
                Number(response?.credit?.remaining_credits || 0) -
                  responseReservedCredits,
                0
              );
        handeleUserDetails({
          ...userDetails,
          credit_tier: response?.credit?.tier || null,
          credit_remaining: response?.credit?.remaining_credits,
          credit_reserved: responseReservedCredits,
          credit_available: responseAvailableCredits,
          credit_tier_costs: response?.credit?.tier_costs || null,
        });
        setCreditInputError("");
      }
    } finally {
      setIsSavingAccountCredit(false);
    }
  };

  const handleStatusToggle = async (event) => {
    const nextStatus = event.target.checked;
    try {
      const response = await dispatch(
        requestUpdateUserStatus(
          page,
          searchText,
          enterprise,
          status,
          type,
          userDetails?.public_id,
          nextStatus
        )
      );

      const nextDetails = {
        ...userDetails,
        status: nextStatus,
      };

      if (response?.credit) {
        nextDetails.credit_tier = response.credit?.tier ?? null;
        nextDetails.credit_remaining = response.credit?.remaining_credits ?? null;
        nextDetails.credit_reserved = response.credit?.reserved_credits ?? 0;
        nextDetails.credit_available = response.credit?.available_credits ?? null;
        nextDetails.credit_tier_costs = response.credit?.tier_costs ?? null;
      } else if (nextStatus) {
        if (!nextDetails.credit_tier) {
          nextDetails.credit_tier = "tier_1";
        }
        if (
          nextDetails.credit_remaining === null ||
          nextDetails.credit_remaining === undefined ||
          nextDetails.credit_remaining === ""
        ) {
          nextDetails.credit_remaining = 20;
        }
        if (
          nextDetails.credit_reserved === null ||
          nextDetails.credit_reserved === undefined ||
          nextDetails.credit_reserved === ""
        ) {
          nextDetails.credit_reserved = 0;
        }
        nextDetails.credit_available = Math.max(
          Number(nextDetails.credit_remaining || 0) -
            Number(nextDetails.credit_reserved || 0),
          0
        );
      }

      handeleUserDetails(nextDetails);

      if (nextStatus) {
        setCreditForm({
          tier: nextDetails.credit_tier || "tier_1",
          remaining_credits:
            nextDetails.credit_remaining !== null &&
            nextDetails.credit_remaining !== undefined
              ? Number(nextDetails.credit_remaining)
              : "",
        });
        if (
          Number(nextDetails.credit_remaining) < Number(nextDetails.credit_reserved || 0)
        ) {
          setCreditInputError(
            `Current credits cannot be lower than reserved credits (${Number(
              nextDetails.credit_reserved || 0
            )}).`
          );
        } else {
          setCreditInputError("");
        }
      }
    } catch (error) {
      // errors are already handled by global feedback actions
    }
  };

  const statusBlock = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: isMobile ? "space-between" : "flex-end",
        gap: 1,
        width: isMobile ? "100%" : "auto",
        mb: isMobile ? 1.5 : 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.7,
          px: 1.2,
          py: 0.65,
          borderRadius: "999px",
          backgroundColor: userDetails?.status ? "#CFF6EE" : "#FCE3CF",
          maxWidth: "100%",
        }}
      >
        {userDetails?.status ? (
          <AccountConfirmed color="#14B8A6" />
        ) : (
          <ConfirmationPending />
        )}
        <Typography
          variant="title9"
          sx={{ fontSize: "14px", whiteSpace: "nowrap" }}
          color={userDetails?.status ? "#14B8A6" : "#D9822F"}
        >
          {userDetails?.status ? t("account_activated") : t("account_deactivated")}
        </Typography>
      </Box>
      {isEnterpriseAccount && (
        <IOSSwitch
          color="success"
          checked={userDetails?.status}
          onChange={handleStatusToggle}
        />
      )}
    </Box>
  );

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        PaperProps={{
          sx: !isMobile
            ? {
                width: "34%",
                maxWidth: "680px",
                minWidth: "560px",
                padding: "18px 20px",
                borderRadius: "20px",
              }
            : {
                width: "92%",
                maxHeight: "80%",
                margin: "5px 0",
                padding: "8px",
                borderRadius: "20px",
              },
        }}
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          <IconButton onClick={handleClose}>
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
            }}
          >
            {isMobile && statusBlock}
            <Grid container alignItems="center">
              <Grid
                item
                xs={isMobile ? 12 : 10}
                align="left"
              >
                <Stack>
                  <Typography variant="title8" sx={{ fontSize: "20px" }}>
                    {userDetails?.username}
                  </Typography>
                  <Typography variant="title9" sx={{ fontSize: "14px" }}>
                    {userDetails?.email}
                  </Typography>
                  {userDetails?.orga_profile_name !==
                    userDetails?.enterprise_name && (
                    <Typography variant="subtitle4">
                      {t(userDetails?.enterprise_name)}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>
            {!isMobile && statusBlock}
          </div>
        </DialogTitle>
        <DialogContent sx={{ marginTop: "10px" }}>
          <>
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                marginTop: "10px",
              }}
            >
              <Typography variant="title9">{t("creation_date")}</Typography>
            </div>
            <Typography variant="subtitle7">
              {moment(userDetails?.creation_date, "DD/MM/YYYY HH:mm:ss").format(
                "DD-MM-YYYY"
              )}
            </Typography>
          </>
          {isEnterpriseAccount && (
            <Box sx={{ mt: 3 }}>
              {!hasPersistedCreditValues && !userDetails?.status ? (
                <Typography
                  variant="subtitle4"
                  sx={{ color: "#D9822F", mt: 0.5, fontWeight: 500 }}
                >
                  {t("activate_account_to_initialize_credits")}
                </Typography>
              ) : shouldShowCreditControls ? (
                <>
                  <Typography variant="title9" sx={{ mb: 0.25 }}>
                    Credit Plan Settings
                  </Typography>
                  <Grid
                    container
                    spacing={2}
                    mt={0.4}
                    alignItems="stretch"
                  >
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "#F8FAFC",
                            height: "48px",
                          },
                        }}
                      >
                        <InputLabel>{t("credit_tier")}</InputLabel>
                        <Select
                          label={t("credit_tier")}
                          value={creditForm.tier}
                          disabled={!canEditCredits || !userDetails?.status}
                          onChange={(event) =>
                            setCreditForm((prev) => ({
                              ...prev,
                              tier: event.target.value,
                            }))
                          }
                        >
                          <MenuItem value="tier_1">{t("tier_1")}</MenuItem>
                          <MenuItem value="tier_2">{t("tier_2")}</MenuItem>
                          <MenuItem value="tier_3">{t("tier_3")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t("initial_credits", { defaultValue: "Current Credits" })}
                        value={creditForm.remaining_credits}
                        disabled={!canEditCredits || !userDetails?.status}
                        inputProps={{ min: 0 }}
                        error={Boolean(creditInputError)}
                        helperText={creditInputError}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: hasZeroCurrentCredits ? "#FFF7F7" : "#F8FAFC",
                            height: "48px",
                            "& fieldset": {
                              borderColor: hasZeroCurrentCredits ? "#F5B5B5" : undefined,
                            },
                            "&:hover fieldset": {
                              borderColor: hasZeroCurrentCredits ? "#EF4444" : undefined,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: hasZeroCurrentCredits ? "#DC2626" : undefined,
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: hasZeroCurrentCredits ? "#991B1B" : undefined,
                          },
                          "& .MuiInputBase-input": {
                            color: hasZeroCurrentCredits ? "#7F1D1D" : undefined,
                            fontWeight: hasZeroCurrentCredits ? 700 : undefined,
                          },
                        }}
                        onKeyDown={(event) => {
                          if (["-", "+", "e", "E"].includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        onChange={(event) =>
                          setCreditForm((prev) => {
                            const value = event.target.value;
                            if (value === "") {
                              setCreditInputError("");
                              return { ...prev, remaining_credits: "" };
                            }

                            const parsed = Number(value);
                            if (!Number.isFinite(parsed)) {
                              return prev;
                            }

                            if (parsed < 0) {
                              setCreditInputError("Impossible: value must be 0 or greater.");
                              return prev;
                            }

                            if (parsed < reservedCredits) {
                              setCreditInputError(
                                `Current credits cannot be lower than reserved credits (${reservedCredits}).`
                              );
                            } else {
                              setCreditInputError("");
                            }

                            return {
                              ...prev,
                              remaining_credits: parsed,
                            };
                          })
                        }
                      />
                    </Grid>
                    {showReservedCreditField && (
                      <Grid item xs={12} md={showRemainingCreditField ? 6 : 12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t("reserved_credits", { defaultValue: "Reserved Credits" })}
                          value={reservedCredits}
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "#F8FAFC",
                              height: "48px",
                            },
                          }}
                        />
                      </Grid>
                    )}
                    {showRemainingCreditField && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t("remaining_after_reports_generation", {
                            defaultValue: "Remaining After Report Generation",
                          })}
                          value={remainingAfterReportsGeneration}
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "#F8FAFC",
                              height: "48px",
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Box
                    sx={{
                      mt: 1.75,
                      display: "flex",
                      justifyContent: isMobile ? "stretch" : "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      fullWidth={isMobile}
                      disabled={
                        !canEditCredits ||
                        !userDetails?.status ||
                        Boolean(creditInputError) ||
                        (nextRemainingCredits !== null &&
                          nextRemainingCredits < reservedCredits) ||
                        isSavingAccountCredit
                      }
                      onClick={handleSaveAccountCredit}
                      sx={{
                        backgroundColor: "#14B8A6",
                        color: "#FFFFFF",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontSize: "15px",
                        fontWeight: 600,
                        py: 0.85,
                        px: 2.5,
                        minWidth: isMobile ? "100%" : "220px",
                        "&:hover": { backgroundColor: "#0D9488" },
                        "&.Mui-disabled": {
                          backgroundColor: "#E5E7EB",
                          color: "#9CA3AF",
                        },
                      }}
                    >
                      Save Credit Changes
                    </Button>
                  </Box>
                </>
              ) : null}
            </Box>
          )}
          {userDetails?.type === "free" && (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "20px",
                }}
              >
                {" "}
                <Typography variant="title9">{t("manged_by")}</Typography>
                <Typography variant="subtitle7">
                  {userDetails?.managed_by}
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "20px",
                }}
              >
                <Typography variant="title9">{t("user_orga")}</Typography>
                <Typography variant="subtitle7">
                  {userDetails?.orga_profile_name}
                </Typography>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default UserDetails;
